// Pure logica voor "Mijn deadlines" op /projects (DeadlineOverview).
//
// Joris (25 sep 2026): "Het is niet helemaal duidelijk wat deze deadlines zijn van welk
// project." Drie oorzaken, hier opgelost:
//   1. Deliverables uit /Api/my-deadlines hebben een lege `projectName` (alleen nummer en id);
//      taakrijen hebben "9252. Full Frame - Algemene interne uren 2026". `projectRegel` maakt
//      daar één leesbare regel van: "9252 · Full Frame — Algemene interne uren 2026".
//   2. Met includeStart komt elke taak twee keer terug (`<id>:start` en `<id>:eind`).
//      `voegTakenSamen` maakt er één rij van: de oplevering is leidend, de start alleen een
//      stille hint als die nog moet komen.
//   3. Honderden oude verlopen items (nooit afgevinkt) duwen de rest weg. `splitsVerlopen`
//      houdt de laatste 30 dagen in beeld; de rest gaat achter een klik.

export const VERLOPEN_GRENS = 30;

const AFGEROND = new Set(['Afgerond', 'Opgestuurd', 'Gearchiveerd']);

const schoon = (v) => (v === null || v === undefined ? '' : String(v).trim());

/**
 * Projectindex uit de projectenlijst van ProjectList (Airtable-vorm `{ id, fields }`):
 * id → { nummer, klant, naam }. Klant volgt dezelfde afleiding als de projecttabel.
 */
export function maakProjectIndex(projecten = [], klantVan = () => '') {
  const index = new Map();
  (projecten || []).forEach((p) => {
    if (!p?.id) return;
    const f = p.fields || {};
    const volledig = schoon(f.Projectnr_en_naam);
    const naam = schoon(f.Projectnaam) || volledig.replace(/^[^.]*\.\s*/, '');
    index.set(p.id, { nummer: schoon(f.Projectnummer), klant: schoon(klantVan(f)), naam });
  });
  return index;
}

/** "9252. Full Frame - Algemene interne uren" → { nummer, klant, naam }. */
export function ontleedProjectNaam(tekst, nummer = '') {
  const t = schoon(tekst);
  const m = t.match(/^(\d+)\.\s*(.*)$/);
  const nr = schoon(nummer) || (m ? m[1] : '');
  const rest = m ? m[2] : t;
  const knip = rest.indexOf(' - ');
  if (knip > 0) return { nummer: nr, klant: rest.slice(0, knip).trim(), naam: rest.slice(knip + 3).trim() };
  return { nummer: nr, klant: '', naam: rest };
}

/**
 * Leesbare projectregel voor een item. Volgorde van bronnen: projectindex (beste: echte
 * klant- en projectnaam), dan de projectnaam van het item zelf, dan die van een ander item
 * van hetzelfde project (deliverables krijgen geen naam mee), dan alleen het nummer.
 * Zonder project: `{ leeg: true, tekst: 'geen project' }`.
 */
export function projectRegel(item, { index, namen } = {}) {
  const pid = item?.projectId;
  const uitIndex = pid && index?.get(pid);
  let delen;
  if (uitIndex && (uitIndex.naam || uitIndex.klant)) {
    delen = { ...uitIndex, nummer: uitIndex.nummer || schoon(item.projectNumber) };
  } else {
    const naamTekst = schoon(item?.projectName) || (pid && namen?.get(pid)) || '';
    delen = ontleedProjectNaam(naamTekst, item?.projectNumber);
  }
  const { nummer, klant, naam } = delen;
  if (!nummer && !klant && !naam) return { leeg: true, tekst: 'geen project', nummer: '', klant: '', naam: '' };
  const titel = [klant, naam].filter(Boolean).join(' — ');
  return { leeg: false, tekst: [nummer, titel].filter(Boolean).join(' · '), nummer, klant, naam };
}

/** projectId → projectnaam uit de items die er één hebben (voor deliverables zonder naam). */
export function namenPerProject(items = []) {
  const namen = new Map();
  items.forEach((i) => {
    if (i?.projectId && schoon(i.projectName) && !namen.has(i.projectId)) namen.set(i.projectId, schoon(i.projectName));
  });
  return namen;
}

/**
 * Eén rij per taak. Invoer: `byDate` van /Api/my-deadlines (met includeStart).
 * Uit: items met `soort` ('taak' | 'deliverable'), `datum`, `dagen` (hoofdmoment),
 * voor taken ook `start`/`eind` ({ datum, dagen } of null), `alleenStart` (geen eindmoment
 * in het venster) en `startHint` (startdatum die nog moet komen en anders is dan de hoofddatum).
 * Gesorteerd op `dagen`.
 */
export function voegTakenSamen(items = []) {
  const taken = new Map();
  const uit = [];
  items.forEach((i) => {
    if (!i) return;
    if (i.type !== 'task') {
      uit.push({ ...i, soort: 'deliverable', datum: i.deadline, dagen: i.daysUntil });
      return;
    }
    const id = String(i.id).split(':')[0];
    let t = taken.get(id);
    if (!t) {
      const { moment, deadline, daysUntil, ...rest } = i;
      t = { ...rest, id, soort: 'taak', start: null, eind: null };
      taken.set(id, t);
      uit.push(t);
    }
    const moment = { datum: i.deadline, dagen: i.daysUntil };
    if (i.moment === 'start') t.start = moment;
    else t.eind = moment;
  });
  taken.forEach((t) => {
    const hoofd = t.eind || t.start;
    t.datum = hoofd.datum;
    t.dagen = hoofd.dagen;
    t.alleenStart = !t.eind;
    t.startHint = t.eind && t.start && t.start.dagen > 0 && t.start.dagen < t.eind.dagen ? t.start.datum : null;
  });
  return uit.sort((a, b) => a.dagen - b.dagen);
}

/** Tellers voor de filters. `start` = taken waarvan de start nog moet komen (vanaf vandaag). */
export function telSoorten(samen = []) {
  const c = { alles: samen.length, taak: 0, deliverable: 0, start: 0 };
  samen.forEach((i) => {
    c[i.soort] += 1;
    if (i.soort === 'taak' && i.start && i.start.dagen >= 0) c.start += 1;
  });
  return c;
}

/**
 * Filter op soort. 'start' toont taken die nog moeten beginnen, gesorteerd en gegroepeerd
 * op hun startdatum (dat is dan het moment waarop de PL input moet geven).
 */
export function filterSoort(samen = [], soort = 'alles') {
  if (soort === 'taak' || soort === 'deliverable') return samen.filter((i) => i.soort === soort);
  if (soort === 'start') {
    return samen
      .filter((i) => i.soort === 'taak' && i.start && i.start.dagen >= 0)
      .map((i) => ({ ...i, datum: i.start.datum, dagen: i.start.dagen, opStart: true, startHint: null }))
      .sort((a, b) => a.dagen - b.dagen);
  }
  return samen;
}

/** Verlopen items ouder dan `grens` dagen apart: die zijn waarschijnlijk vergeten af te vinken. */
export function splitsVerlopen(items = [], grens = VERLOPEN_GRENS) {
  const recent = [];
  const oud = [];
  items.forEach((i) => { (i.dagen < -grens ? oud : recent).push(i); });
  return { recent, oud };
}

export const DATUMGROEPEN = [
  ['verlopen', 'Verlopen'],
  ['vandaag', 'Vandaag'],
  ['dezeWeek', 'Deze week'],
  ['volgendeWeek', 'Volgende week'],
  ['later', 'Later'],
];

export const datumGroep = (dagen) => {
  if (dagen < 0) return 'verlopen';
  if (dagen === 0) return 'vandaag';
  if (dagen <= 7) return 'dezeWeek';
  if (dagen <= 14) return 'volgendeWeek';
  return 'later';
};

/** [{ sleutel, label, items }] in vaste volgorde, lege groepen weg. */
export function groepeerOpDatum(items = []) {
  const per = Object.fromEntries(DATUMGROEPEN.map(([k]) => [k, []]));
  items.forEach((i) => per[datumGroep(i.dagen)].push(i));
  return DATUMGROEPEN.filter(([k]) => per[k].length).map(([sleutel, label]) => ({ sleutel, label, items: per[sleutel] }));
}

/**
 * [{ projectId, regel, items }] — projecten met de eerstvolgende niet-verlopen deadline
 * bovenaan; projecten met alleen verlopen items daarna (meest recent verlopen eerst).
 */
export function groepeerOpProject(items = [], opties = {}) {
  const per = new Map();
  items.forEach((i) => {
    const pid = i.projectId || 'geen';
    if (!per.has(pid)) per.set(pid, { projectId: pid, regel: projectRegel(i, opties), items: [] });
    per.get(pid).items.push(i);
  });
  const sleutel = (g) => {
    const komend = g.items.filter((i) => i.dagen >= 0).map((i) => i.dagen);
    if (komend.length) return Math.min(...komend);
    return 100000 - Math.max(...g.items.map((i) => i.dagen));
  };
  return [...per.values()].sort((a, b) => sleutel(a) - sleutel(b));
}

/** Is een taak lokaal afgevinkt (na een schrijfactie in het paneel of via "afvinken?")? */
export const isAfgevinkt = (velden) => Boolean(velden) && (velden.Afgerond === true || AFGEROND.has(velden.PrioNr));

/** Samengevoegde taak → vorm voor het TaakPaneel (zie ui-components/TaakPaneel/naarTaak). */
export function naarPaneelTaak(t) {
  return {
    id: t.id,
    name: t.name,
    status: t.status || '',
    startDate: t.startDate,
    endDate: t.endDate,
    deadline: t.taskDeadline,
    deadlineType: t.deadlineType,
    projectId: t.projectId,
    projectName: t.projectName,
    deliverableId: t.deliverableId,
    assignee: schoon(t.assignee) ? schoon(t.assignee).split(/\s*,\s*/) : [],
  };
}
